import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import type { DealView } from '@/lib/db/deals';
import { DealMediaBadges } from '@/components/DealMediaBadges';
import { ImagePlaceholder } from '@/components/ImagePlaceholder';
import { StatusPill } from '@/components/StatusPill';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { formatAed, formatNumber } from '@/lib/format';
import { usesOriginalPriceReference } from '@/lib/discount-display';
import './DealCard.css';

export type DealCardProps = {
  deal: DealView;
  showWhatsAppCta?: boolean;
  /**
   * Resolved WhatsApp number for this deal's advisor (assigned → default
   * cascade, see `useAdvisorDirectory`). When the CTA is enabled but this is
   * `undefined`, the button is HIDDEN rather than falling back to the agency
   * number — the card never points to the wrong contact.
   */
  whatsappPhone?: string;
  // ADR-025 §A — opt-in remove button surfaced on DashboardSavedPage only.
  // Sibling of the main <Link> (never nested inside it) so click semantics
  // and a11y stay clean.
  showRemoveButton?: boolean;
  onRemove?: () => void;
};

// ADR-017: WhatsApp CTA opt-in via prop, only enabled on /deals catalogue.
// Uses window.location.origin so the URL embedded in the message reflects the
// active environment (Netlify preview / prod) without a build-time env var.
function composeWhatsAppMessage(deal: DealView, t: TFunction): string {
  const dealUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/deals/${deal.slug}`
      : `/deals/${deal.slug}`;
  return t('common:dealCard.whatsappMessage', {
    buildingTitle: deal.buildingTitle,
    referenceId: deal.referenceId,
    dealUrl,
  });
}

// Editorial deal card — surfaced on Home (Selection of the Week) and on the
// Catalogue list (lot L7.3). Visual reference: ddm-public.jsx + ddm-components.jsx.
export function DealCard({
  deal,
  showWhatsAppCta = false,
  whatsappPhone,
  showRemoveButton = false,
  onRemove,
}: DealCardProps) {
  const { t } = useTranslation();
  // Off-plan deals are benchmarked against their original purchase price, never
  // DLD — so the reference label is type-driven (the value is already the
  // original price for off-plan, copied by the DB trigger). The discount badge
  // itself is rendered by <DealMediaBadges> (#263).
  const benchLabelKey = usesOriginalPriceReference(deal.dealType)
    ? 'common:dealCard.originalPrice'
    : 'common:dealCard.dldBenchmark';
  return (
    <article className="deal-card">
      <Link to={`/deals/${deal.slug}`} className="deal-card__main" aria-label={deal.buildingTitle}>
        <div className="deal-card__media">
          {deal.imageUrl ? (
            <img
              src={deal.imageUrl}
              alt={deal.imageLabel}
              loading="lazy"
              decoding="async"
              className="deal-card__img"
            />
          ) : (
            <ImagePlaceholder label={deal.imageLabel} />
          )}
          <DealMediaBadges
            propertyType={deal.propertyType}
            discountPct={deal.discountPct}
            dldConfidence={deal.dldConfidence}
            dldScope={deal.dldScope}
            size="card"
          />
        </div>
        <div className="deal-card__body">
          <div className="deal-card__row">
            <StatusPill status={deal.status} />
            <span className="deal-card__community">{deal.community}</span>
          </div>
          <h3 className="deal-card__title">{deal.buildingTitle}</h3>
          <div className="deal-card__meta">
            <span>
              {deal.bedrooms} {t('common:dealCard.br')}
            </span>
            <span aria-hidden="true">·</span>
            <span>
              {formatNumber(deal.areaSqft)} {t('common:dealCard.sqft')}
            </span>
          </div>
          <div className="deal-card__price-row">
            <div>
              <div className="deal-card__price-label">{t('common:dealCard.askingPrice')}</div>
              <div className="deal-card__price">{formatAed(deal.askingAed)}</div>
            </div>
            <div className="deal-card__bench">
              <div className="deal-card__bench-label">{t(benchLabelKey)}</div>
              <div className="deal-card__bench-value">{formatAed(deal.dldReferenceAed)}</div>
            </div>
          </div>
        </div>
      </Link>
      {showWhatsAppCta && whatsappPhone && (
        <WhatsAppButton
          fullWidth
          size="md"
          className="deal-card__cta"
          label={t('common:dealCard.chatOnWhatsapp')}
          message={composeWhatsAppMessage(deal, t)}
          phone={whatsappPhone}
        />
      )}
      {showRemoveButton && (
        <button
          type="button"
          className="deal-card__remove"
          onClick={onRemove}
          aria-label={t('common:dealCard.remove')}
        >
          {t('common:dealCard.remove')}
        </button>
      )}
    </article>
  );
}
